import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  alpha,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon, MicNone as MicIcon } from '@mui/icons-material';
import MultipleSelect from './MultipleSelect';
import LoadingSpinner from './LoadingSpinner';
import { Editor } from '@tinymce/tinymce-react';
import HRService from '../../services/HrService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

const PolicyNameField = memo(({ value, onChange }) => {
  return (
    <TextField
      fullWidth
      label="Policy Name"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Enter the policy name..."
      size="small"
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 1,
        }
      }}
    />
  );
});

const AIDocumentationDialog = ({
  open,
  onClose,
  onSubmit,
  processOwners,
  setProcessOwners,
  dateOfInquiry,
  setDateOfInquiry,
  policyAvailable,
  setPolicyAvailable,
  evidenceReviewed,
  setEvidenceReviewed,
  evidenceDetails,
  setEvidenceDetails,
  evidenceSufficient,
  setEvidenceSufficient,
  sampleReviewed,
  setSampleReviewed,
  sampleCount,
  setSampleCount,
  isGenerating
}) => {
  const [processOwnerOptions, setProcessOwnerOptions] = useState([]);
  const [isLoadingProcessOwners, setIsLoadingProcessOwners] = useState(true);
  const [localPolicyName, setLocalPolicyName] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProcessOwners = async () => {
      try {
        setIsLoadingProcessOwners(true);
        const response = await HRService.fetchAllHRRecords();
        const owners = response.map(record => ({
          value: record.EMAIL_ADDRESS,
          label: `${record.FIRST_NAME} ${record.LAST_NAME} - ${record.JOB_TITLE}`
        }));
        setProcessOwnerOptions(owners);
      } catch (error) {
        console.error('Error fetching process owners:', error);
        setProcessOwnerOptions([]);
      } finally {
        setIsLoadingProcessOwners(false);
      }
    };

    fetchProcessOwners();
  }, []);

  const handleEditorBlur = (e, editor) => {
    const content = editor.getContent();
    if (content !== evidenceDetails) {
      setEvidenceDetails(content);
    }
  };

  const handleAudioUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (50MB = 50 * 1024 * 1024 bytes)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (file.size > maxSize) {
      console.error('File is too large. Maximum size is 50MB');
      alert('File is too large. Maximum size is 50MB');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsTranscribing(true);
    const formData = new FormData();
    formData.append('audio_file', file);

    try {
      const response = await fetch(`${BACKEND_URL}/api/audit/transcribe-audio/`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to transcribe audio');
      }

      const data = await response.json();
      const editorInstance = window.tinymce.get('design-description-editor');
      if (editorInstance) {
        const currentContent = editorInstance.getContent();
        editorInstance.setContent(currentContent + '<br/>' + data.documentation);
        setEvidenceDetails(editorInstance.getContent());
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
    } finally {
      setIsTranscribing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Evidence types commonly used in audits
  const evidenceTypeOptions = [
    { value: 'policy', label: 'Policy Documentation' },
    { value: 'procedure', label: 'Procedure Manual' },
    { value: 'configuration', label: 'System Configuration' },
    { value: 'screenshots', label: 'System Screenshots' },
    { value: 'reports', label: 'System Reports' },
    { value: 'logs', label: 'System Logs' },
    { value: 'emails', label: 'Email Communications' },
    { value: 'meeting_notes', label: 'Meeting Notes/Minutes' },
    { value: 'training', label: 'Training Materials' },
    { value: 'workflow', label: 'Workflow Documentation' },
    { value: 'access_matrix', label: 'Access Matrix/Rights' },
    { value: 'org_chart', label: 'Organization Charts' },
    { value: 'job_descriptions', label: 'Job Descriptions' },
    { value: 'contracts', label: 'Contracts/Agreements' },
    { value: 'certifications', label: 'Certifications' },
    { value: 'audit_logs', label: 'Audit Logs' },
    { value: 'test_results', label: 'Test Results' },
    { value: 'change_records', label: 'Change Management Records' },
    { value: 'incident_records', label: 'Incident Records' },
    { value: 'risk_assessments', label: 'Risk Assessments' }
  ];

  const isFormValid = () => {
    return (
      processOwners.length > 0 &&
      dateOfInquiry &&
      policyAvailable &&
      (policyAvailable === 'No' || (policyAvailable === 'Yes' && localPolicyName)) &&
      evidenceReviewed.length > 0 &&
      evidenceDetails &&
      evidenceSufficient &&
      (sampleReviewed === 'No' || (sampleReviewed === 'Yes' && sampleCount))
    );
  };

  const handleSubmit = () => {
    onSubmit({
      processOwners,
      dateOfInquiry,
      policyAvailable,
      policyName: localPolicyName,
      evidenceReviewed,
      evidenceDetails,
      evidenceSufficient,
      sampleReviewed,
      sampleCount: sampleReviewed === 'Yes' ? sampleCount : null
    });
  };

  const StyledSection = ({ title, children }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: `0 0 0 1px ${alpha('#1976d2', 0.1)}`,
        }
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          mb: 2,
          color: 'text.primary',
          fontWeight: 600,
          fontSize: '0.875rem',
          letterSpacing: '0.01em'
        }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );

  return (
    <Dialog
      open={open}
      onClose={!isGenerating ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 2.5,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h6" component="span" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
          Generate Design Documentation
        </Typography>
      </DialogTitle>

      {isGenerating ? (
        <LoadingSpinner />
      ) : (
        <>
          <DialogContent sx={{ px: 3, py: 3 }}>
            <Stack spacing={2.5}>
              <StyledSection title="Process Owner">
                <MultipleSelect
                  isMultiSelect={true}
                  placeholderText={isLoadingProcessOwners ? "Loading process owners..." : "Select process owners..."}
                  selectOptions={processOwnerOptions}
                  selectedOptions={processOwners}
                  handleChange={(selected) => setProcessOwners(selected)}
                  isDisabled={isLoadingProcessOwners}
                />
              </StyledSection>

              <StyledSection title="Date of Inquiry">
                <TextField
                  type="date"
                  fullWidth
                  value={dateOfInquiry}
                  onChange={(e) => setDateOfInquiry(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                />
              </StyledSection>

              <StyledSection title="Design Description">
                <Box sx={{ 
                  position: 'relative',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  '& .tox-tinymce': {
                    border: 'none'
                  }
                }}>
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    right: 8, 
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'background.paper',
                    borderRadius: '50%',
                    boxShadow: 1
                  }}>
                    {isTranscribing ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Tooltip title="Upload audio recording (max 50MB)">
                        <label>
                          <input
                            type="file"
                            style={{ display: 'none' }}
                            accept="audio/*"
                            onChange={handleAudioUpload}
                            ref={fileInputRef}
                            disabled={isTranscribing}
                          />
                          <IconButton
                            component="span"
                            disabled={isTranscribing}
                            size="small"
                            sx={{
                              '&:hover': {
                                bgcolor: alpha('#000', 0.04),
                              }
                            }}
                          >
                            <MicIcon fontSize="small" />
                          </IconButton>
                        </label>
                      </Tooltip>
                    )}
                  </Box>
                  <Editor
                    apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                    id="design-description-editor"
                    initialValue={evidenceDetails}
                    onBlur={handleEditorBlur}
                    init={{
                      height: 200,
                      menubar: false,
                      statusbar: false,
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'charmap',
                        'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'table', 'code', 'help', 'wordcount'
                      ],
                      toolbar: 'blocks | bold italic | bullist numlist | removeformat | help',
                      content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; font-size: 14px; }',
                      placeholder: 'Provide a description of the control design...',
                      skin: 'oxide',
                      branding: false
                    }}
                  />
                </Box>
              </StyledSection>

              <StyledSection title="Policy Available">
                <Stack spacing={2}>
                  <RadioGroup
                    row
                    value={policyAvailable}
                    onChange={(e) => {
                      setPolicyAvailable(e.target.value);
                      if (e.target.value === 'No') {
                        setLocalPolicyName('');
                      }
                    }}
                  >
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>
                  
                  {policyAvailable === 'Yes' && (
                    <TextField
                      fullWidth
                      autoFocus
                      label="Policy Name"
                      value={localPolicyName}
                      onChange={(e) => setLocalPolicyName(e.target.value)}
                      placeholder="Enter the policy name..."
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                        }
                      }}
                    />
                  )}
                </Stack>
              </StyledSection>

              <StyledSection title="Types of Evidence Reviewed">
                <MultipleSelect
                  isMultiSelect={true}
                  placeholderText="Select evidence types..."
                  selectOptions={evidenceTypeOptions}
                  selectedOptions={evidenceReviewed}
                  handleChange={(selected) => setEvidenceReviewed(selected)}
                />
              </StyledSection>

              <StyledSection title="Is sample reviewed as part of the corroboration">
                <Stack spacing={2}>
                  <RadioGroup
                    row
                    value={sampleReviewed}
                    onChange={(e) => {
                      setSampleReviewed(e.target.value);
                      if (e.target.value === 'No') {
                        setSampleCount('');
                      }
                    }}
                  >
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>

                  {sampleReviewed === 'Yes' && (
                    <TextField
                      type="number"
                      fullWidth
                      autoFocus
                      label="Number of Samples"
                      value={sampleCount}
                      onChange={(e) => setSampleCount(e.target.value)}
                      placeholder="Enter number of samples..."
                      size="small"
                      InputProps={{ inputProps: { min: 1 } }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                        }
                      }}
                    />
                  )}
                </Stack>
              </StyledSection>

              <StyledSection title="Is evidence sufficient to conclude that the design is effective">
                <RadioGroup
                  row
                  value={evidenceSufficient}
                  onChange={(e) => setEvidenceSufficient(e.target.value)}
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              </StyledSection>
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <Button
              onClick={onClose}
              disabled={isGenerating}
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isGenerating || !isFormValid()}
              startIcon={<AutoAwesomeIcon />}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none',
                  bgcolor: 'primary.dark'
                }
              }}
            >
              {isGenerating ? 'Generating...' : 'Generate Documentation'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default AIDocumentationDialog; 